import './styles.css';
import './hud.css';
import * as THREE from 'three';
import { World, emptyCommand } from './sim/world';
import { WEATHER_KINDS } from './sim/weather';
import { WorldView, QUALITY } from './render/view';
import { CAMERA_MODES, type CameraMode } from './render/cameras';
import { Effects } from './render/effects/effects';
import { Environment } from './render/environment';
import { AimView } from './render/aim';
import { ThreatView } from './render/threat';
import { LAYER_OPAQUE, LAYER_WATER } from './render/pipeline';
import { AudioEngine } from './audio/audio';
import { Input } from './ui/input';
import { Hud } from './ui/hud';
import { clamp } from './core/math';
import { FullscreenPass } from './render/gpu';
import { NOISE3D_FRAG } from './render/sky/cloudGlsl';
import { Art } from './render/ship/art';
import { SHIP_CLASSES, type AmmoId } from './data/classes';
import { CANNONS, MAX_HALF_BEAM } from './data/ship';
import type { Vessel } from './sim/vessel';
import { hostile } from './sim/captain';
import { ELEVATION_MAX } from './sim/cannons';
import { AIM_PARTS, flyShot, makeFall, muzzleSpeed, shotDrag, type AimPart } from './sim/gunnery';
import { Vec3 } from './core/math';
import { weatherTuning } from './render/materials/materials';

const canvas = document.getElementById('view') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui')!;

const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', stencil: false });
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.autoUpdate = false;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;

const params = new URLSearchParams(location.search);
const world = new World({
  enemies: params.get('enemies') !== '0',
  difficulty: (['easy', 'normal', 'hard', 'legendary'] as const).find((d) => d === params.get('difficulty')) ?? 'normal',
});
const audio = new AudioEngine();
const cmd = emptyCommand();
let view: WorldView;
let effects: Effects;
let aimView: AimView;
let threatView: ThreatView;
let env: Environment;
let input: Input;
let helm = 0;
let started = false;
let renderScale = 1;
let frameAvg = 1 / 60;
let scaleTimer = 0;
let fpsAvg = 60;

const hud = new Hud(uiRoot, world, {
  setWeather: (k) => world.weather.set(k, 25),
  setDynamicWeather: (on) => (world.weather.dynamic = on),
  setHours: (h) => world.day.setHours(h),
  setTimeScale: (s) => (world.day.timeScale = s),
  setGameSpeed: (s) => (world.speed = s),
  setWaveIntensity: (v) => (world.settings.waveIntensity = v),
  setWind: (s) => (world.settings.windSpeed = s),
  setQuality: (k) => {
    view.setQuality(QUALITY[k]!);
    renderScale = 1;
    afterResize();
  },
  setPost: (k, on) => (view.pipeline.settings[k] = on),
  setVolume: (v) => audio.setVolume(v),
  setCamera: (m) => view.rig.setMode(m, input),
  setSensitivity: (v) => (input.sensitivity = v),
  setEnemies: (on) => (world.encounters.enabled = on),
  setDifficulty: (d) => (world.encounters.difficulty = d),
  spawn: (kind) => world.spawn(kind, 2600),
  // Dev chart and tools: commands like any other.
  teleport: (x, z) => (cmd.teleport = { x, z }),
  dev: (a) => cmd.dev.push(a),
  watch: (id) => {
    if (id === world.player.id) return view.rig.setMode('orbit', input);
    view.rig.watchId = id;
    view.rig.setMode('watch', input);
  },
  // Our ship lost: fly on over the sea from wherever the view is.
  spectate: () => {
    view.rig.setMode('free', input);
    view.rig.overlook(world);
  },
  setFreeSpeed: (v) => (view.rig.freeSpeed = v),
  restart: () => location.reload(),
  // The battle view frames whatever ship we're keeping a glass on.
  track: (id) => {
    if (id >= 0) view.rig.targetId = id;
  },
  start: () => {
    started = true;
    audio.start();
    canvas.focus();
  },
});

function afterResize(): void {
  const p = view.pipeline;
  effects.setDepth(p.opaqueDepth, p.renderWidth, p.renderHeight, view.rig.camera.near, view.rig.camera.far);
}

function resize(): void {
  view.resize(window.innerWidth, window.innerHeight);
  view.setRenderScale(renderScale);
  afterResize();
}

async function init(): Promise<void> {
  // The Blender-built ships, rig, props and crew (?art=0 for the procedural ones).
  hud.setStatus('Loading models…', false);
  const art = params.get('art') === '0' ? null : await Art.load(import.meta.env.BASE_URL + 'models/', Object.keys(SHIP_CLASSES));
  view = new WorldView(renderer, world, art);
  env = new Environment(world, view.shipModel.materials);
  view.scene.add(env.group);
  effects = new Effects(view.scene, world, view.ocean.foam, view.ship);
  effects.inFrame = (v) => view.inFrame(v);
  aimView = new AimView(view.pipeline, renderer);
  threatView = new ThreatView(view.pipeline);
  input = new Input(canvas);
  canvas.tabIndex = 0;
  canvas.addEventListener('click', () => {
    if (view.rig.mode === 'free' || view.rig.mode === 'deck') input.requestLock();
  });
  window.addEventListener('resize', resize);
  resize();
  // Warm up: one frame to create render targets, then compile every material.
  step(1 / 60, true);
  const cam = view.rig.camera;
  cam.layers.enable(LAYER_OPAQUE);
  cam.layers.enable(LAYER_WATER);
  hud.setStatus('Compiling shaders…', false);
  try {
    await renderer.compileAsync(view.scene, cam);
  } catch {
    // Not fatal: shaders compile on first use.
  }
  cam.layers.set(LAYER_OPAQUE);
  view.endWarmup();
  hud.setStatus('Ready', true);
  applyUrlOptions();
  last = performance.now();
  requestAnimationFrame(frame);
}

function handleKeys(dt: number): void {
  const mode = view.rig.mode;
  const walking = mode === 'free' || mode === 'deck';
  const steer = input.axis(walking ? ['ArrowLeft'] : ['ArrowLeft', 'KeyA'], walking ? ['ArrowRight'] : ['ArrowRight', 'KeyD']);
  if (steer !== 0) helm = clamp(helm + steer * 0.9 * dt, -1, 1);
  else helm -= Math.sign(helm) * Math.min(Math.abs(helm), 0.6 * dt);
  cmd.helm = helm;
  const up = input.pressed('ArrowUp') || (!walking && input.pressed('KeyW'));
  const down = input.pressed('ArrowDown') || (!walking && input.pressed('KeyS'));
  if (up) cmd.sailDelta = 1;
  if (down) cmd.sailDelta = -1;
  // Q / E: take aim with that side's guns (again, or Esc: stand down).
  if (input.pressed('KeyQ')) takeAim(aim.side === -1 ? 0 : -1);
  if (input.pressed('KeyE')) takeAim(aim.side === 1 ? 0 : 1);
  if (input.pressed('Escape')) takeAim(0);
  // R: lay the guns on a ship (and lead her), or let her go (R flies the free camera up).
  if (input.pressed('KeyR') && aim.side !== 0 && mode !== 'free') lockOn();
  // F: lay on her waterline, her hull or her rigging (F flies the free camera down).
  if (input.pressed('KeyF') && aim.side !== 0 && mode !== 'free') aimPart = AIM_PARTS[(AIM_PARTS.indexOf(aimPart) + 1) % AIM_PARTS.length]!;
  if (input.pressed('KeyT')) cmd.toggleTrim = true;
  if (input.pressed('KeyG')) cmd.cycleAmmo = true;
  // B: board (or, in a boarding fight, all hands to it and back); K: cut the grapnels.
  if (input.pressed('KeyB')) {
    if (world.player.boardingWith >= 0) cmd.allHands = true;
    else {
      cmd.board = true;
      hud.boardOrder();
    }
  }
  if (input.pressed('KeyK') && world.player.boardingWith >= 0) cmd.cutLines = true;
  // U I O P: order the damage parties to it (or leave them to the officers); J: grog.
  if (input.pressed('KeyU')) cmd.order = 'leaks';
  if (input.pressed('KeyI')) cmd.order = 'fire';
  if (input.pressed('KeyO')) cmd.order = 'canvas';
  if (input.pressed('KeyP')) cmd.order = 'spars';
  if (input.pressed('KeyJ')) cmd.rally = true;
  if (input.pressed('KeyL')) cmd.toggleLights = true;
  if (input.pressed('KeyY')) {
    if (hud.spectating) watchNext();
    else {
      view.rig.cycleTarget(world);
      if (view.rig.mode !== 'battle') view.rig.setMode('battle', input);
    }
  }
  cmd.trim = clamp(cmd.trim + input.axis(['KeyZ'], ['KeyX']) * dt * 0.8, -1, 1);
  for (const c of CAMERA_MODES) if (input.pressed(`Digit${c.key}`)) view.rig.setMode(c.id, input);
  if (input.pressed('KeyC')) {
    const i = CAMERA_MODES.findIndex((c) => c.id === mode);
    view.rig.setMode(CAMERA_MODES[(i + 1) % CAMERA_MODES.length]!.id as CameraMode, input);
  }
  if (input.pressed('BracketLeft')) world.day.setHours(world.day.hours - 1);
  if (input.pressed('BracketRight')) world.day.setHours(world.day.hours + 1);
  if (input.pressed('KeyN')) {
    const i = WEATHER_KINDS.indexOf(world.weather.kind);
    world.weather.set(WEATHER_KINDS[(i + 1) % WEATHER_KINDS.length]!, 20);
  }
  if (input.pressed('Tab')) {
    if (hud.toggleSettings()) input.releaseLock();
  }
  if (input.pressed('KeyH')) hud.toggleHelp();
  if (input.pressed('F2')) hud.toggleDev();
  if (input.pressed('KeyV')) hud.toggleHud();
  if (input.pressed('KeyM')) audio.toggleMute();
}

/**
 * The broadside being laid: which side (0 none), and the mark - held
 * relative to our ship (ahead and to starboard of her centre, in her heading)
 * so it rides with her as the camera does, or fixed in the world for the free
 * camera. It follows the pointer only while the pointer moves (not while it
 * drags the view), so a still hand holds a still mark.
 */
const aim = { side: 0 as 0 | 1 | -1, ahead: 0, right: 0, x: 0, z: 0, world: false, picked: false };
/**
 * The ship the guns are laid on (R), if any: the point of her aimed at, ahead
 * of her centre and to her starboard in her own heading, so it stays on her
 * as she turns. The sim leads it by the time of flight.
 */
let locked: { id: number; ahead: number; right: number } | null = null;
/** Where on a ship the guns are laid (F, or the gun panel). */
let aimPart: AimPart = 'waterline';
hud.aim.onPart = (p) => (aimPart = p);
hud.combat.onOrder = (p) => (cmd.order = p);
hud.combat.onRally = () => (cmd.rally = true);
/**
 * Fine laying (Shift held): the mark moves a quarter as far as the pointer,
 * from where it was when Shift went down - a pixel spans many metres of sea
 * far off near the horizon. `sx, sy`: the screen point of the last pick.
 */
const fine = { on: false, ax: 0, ay: 0, mx: 0, my: 0, sx: 0, sy: 0 };
const FINE = 0.25;

function takeAim(side: 0 | 1 | -1): void {
  aim.side = side;
  aim.picked = false;
  locked = null;
}

const rayDir = new THREE.Vector3();
const ndc = { x: 0, y: 0 };

/**
 * Where the player points, in normalised device coordinates: the pointer
 * (moved a quarter as far with Shift held), or the middle of the view with
 * the pointer locked. False if it hasn't been anywhere on the view yet.
 */
function pointAt(): boolean {
  ndc.x = ndc.y = 0;
  if (input.pointerLocked) return true;
  if (!input.pointerKnown) return false;
  const r = canvas.getBoundingClientRect();
  let px = input.pointerX - r.left, py = input.pointerY - r.top;
  const shift = input.isDown('ShiftLeft') || input.isDown('ShiftRight');
  if (shift && aim.picked) {
    if (!fine.on) Object.assign(fine, { on: true, ax: px, ay: py, mx: fine.sx, my: fine.sy });
    px = fine.mx + (px - fine.ax) * FINE;
    py = fine.my + (py - fine.ay) * FINE;
  } else {
    fine.on = false;
  }
  fine.sx = px;
  fine.sy = py;
  ndc.x = (px / r.width) * 2 - 1;
  ndc.y = -(py / r.height) * 2 + 1;
  return true;
}

/** Put the mark where the pointer (or, with the pointer locked, the middle of the view) meets the sea. */
function pickMark(): void {
  const cam = view.rig.camera;
  const me = world.player.ship;
  if (!pointAt()) {
    // Nowhere to point yet: a cable or two off the beam.
    aim.world = false;
    aim.ahead = 0;
    aim.right = aim.side * 350;
    aim.picked = true;
    return;
  }
  const d = rayDir.set(ndc.x, ndc.y, 0.5).unproject(cam).sub(cam.position).normalize();
  const o = cam.position;
  // Where the ray meets sea level; toward or above the horizon, no further
  // than a little past what the guns can reach (the ring says it's out of
  // range; any further and it only runs off along the horizon).
  const far = reachOf(world.player.battery.ammo) * 1.15;
  const flat = Math.hypot(d.x, d.z) || 1;
  const t = d.y < -1e-4 ? Math.min(-o.y / d.y, 8000 / flat) : 8000 / flat;
  let dx = o.x + d.x * t - me.com.x, dz = o.z + d.z * t - me.com.z;
  const r = Math.hypot(dx, dz);
  if (!Number.isFinite(r)) return;
  if (r > far) (dx *= far / r), (dz *= far / r);
  aim.x = me.com.x + dx;
  aim.z = me.com.z + dz;
  aim.world = view.rig.mode === 'free';
  const h = me.heading, fx = Math.sin(h), fz = -Math.cos(h);
  aim.ahead = dx * fx + dz * fz;
  aim.right = -dx * fz + dz * fx;
  aim.picked = true;
}

/** How far a main-deck gun at full elevation throws this shot (m), flown as the sim flies it. */
const reach = new Map<AmmoId, number>();
function reachOf(ammo: AmmoId): number {
  let r = reach.get(ammo);
  if (r === undefined) {
    const gun = CANNONS.find((c) => c.deck === 'main')!;
    const v = muzzleSpeed(gun, ammo), e = ELEVATION_MAX;
    const f = flyShot(new Vec3(0, 3, 0), new Vec3(Math.cos(e) * v, Math.sin(e) * v, 0), shotDrag(ammo, 1), 0, 0, World.DT, makeFall());
    r = f.ok ? Math.hypot(f.x, f.z) : 2000;
    reach.set(ammo, r);
  }
  return r;
}

/** Her half-length and half-beam on the waterline, m (about; the bowsprit not counted). */
function halfSize(v: Vessel): [number, number] {
  return [19.5 * v.ship.sx, MAX_HALF_BEAM * v.ship.sz];
}

/** A ship the guns can be laid on: another, afloat, in sight, and not far past their reach. */
function layable(v: Vessel): boolean {
  const me = world.player;
  if (v === me || !v.alive || v.ship.sunk || !world.lookout.get(v.id)?.visible) return false;
  return Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z) < reachOf(me.battery.ammo) * 1.5;
}

const scr = new THREE.Vector3();

/**
 * The ship the player points at: one whose hull and rigging cover the
 * pointer on screen (give or take `margin` px, and never less than a small
 * mark however far off she is), the nearest to it of those.
 */
function shipAt(margin: number): Vessel | null {
  if (!pointAt()) return null;
  const cam = view.rig.camera;
  const r = canvas.getBoundingClientRect();
  const px = (ndc.x * 0.5 + 0.5) * r.width, py = (ndc.y * 0.5 + 0.5) * r.height;
  let best: Vessel | null = null, bd = Infinity;
  for (const v of world.vessels) {
    if (!layable(v)) continue;
    const s = v.ship, [hl, hb] = halfSize(v);
    const h = s.heading, fx = Math.sin(h), fz = -Math.cos(h);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, behind = false;
    for (const a of [-hl, hl]) for (const b of [-hb, hb]) for (const y of [0, 24 * s.sy]) {
      const c = scr.set(s.com.x + fx * a - fz * b, y, s.com.z + fz * a + fx * b).project(cam);
      if (c.z > 1) behind = true;
      const sx = (c.x * 0.5 + 0.5) * r.width, sy = (c.y * 0.5 + 0.5) * r.height;
      (x0 = Math.min(x0, sx)), (x1 = Math.max(x1, sx)), (y0 = Math.min(y0, sy)), (y1 = Math.max(y1, sy));
    }
    if (behind) continue;
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const hw = Math.max((x1 - x0) / 2, 12) + margin, hh = Math.max((y1 - y0) / 2, 10) + margin;
    if (Math.abs(px - cx) > hw || Math.abs(py - cy) > hh) continue;
    const d = Math.hypot(px - cx, py - cy);
    if (d < bd) (bd = d), (best = v);
  }
  return best;
}

/** Lay the guns on her, at the part of her pointed at. */
function lockOnto(v: Vessel): void {
  locked = { id: v.id, ahead: 0, right: 0 };
  if (!input.pointerLocked) pickOn(v);
}

/**
 * R: lay the guns on the ship under the pointer (or, with none there, the
 * nearest enemy on the side being laid), at the part of her pointed at; or,
 * laid on one already, let her go and leave the mark where it is.
 */
function lockOn(): void {
  const me = world.player;
  if (locked) {
    const t = world.aimTarget;
    if (t) holdMark(t.leadX, t.leadZ);
    locked = null;
    return;
  }
  let best = shipAt(24), bd = Infinity;
  if (best) {
    lockOnto(best);
    return;
  }
  // Nothing pointed at: the nearest enemy on this side.
  const h = me.ship.heading, rx = Math.cos(h), rz = Math.sin(h);
  for (const v of world.vessels) {
    if (!layable(v) || !(hostile(me, v) || hostile(v, me))) continue;
    const dx = v.ship.com.x - me.ship.com.x, dz = v.ship.com.z - me.ship.com.z;
    if ((dx * rx + dz * rz) * aim.side <= 0) continue;
    const d = Math.hypot(dx, dz);
    if (d < bd) (bd = d), (best = v);
  }
  if (best) locked = { id: best.id, ahead: 0, right: 0 };
}

/**
 * The part of her the pointer is on: where its ray crosses the upright plane
 * through her centre, square to the line of sight (pointing at her topsides
 * or her rigging would otherwise reach the sea far beyond her), kept to her
 * length and a little beyond.
 */
function pickOn(v: Vessel): void {
  if (!locked || !pointAt()) return;
  const cam = view.rig.camera, o = cam.position, s = v.ship;
  const d = rayDir.set(ndc.x, ndc.y, 0.5).unproject(cam).sub(o).normalize();
  let nx = s.com.x - o.x, nz = s.com.z - o.z;
  const n = Math.hypot(nx, nz);
  if (n < 1) return;
  (nx /= n), (nz /= n);
  const den = d.x * nx + d.z * nz;
  if (den < 1e-4) return;
  const t = ((s.com.x - o.x) * nx + (s.com.z - o.z) * nz) / den;
  const along = (o.x + d.x * t - s.com.x) * -nz + (o.z + d.z * t - s.com.z) * nx;
  if (!Number.isFinite(along)) return;
  const ox = -nz * along, oz = nx * along;
  const [hl, hb] = halfSize(v);
  const h = s.heading, fx = Math.sin(h), fz = -Math.cos(h);
  locked.ahead = clamp(ox * fx + oz * fz, -hl - 25, hl + 25);
  locked.right = clamp(-ox * fz + oz * fx, -hb - 15, hb + 15);
}

/** Hold the mark at this world point (relative to our ship, or fixed for the free camera). */
function holdMark(x: number, z: number): void {
  const me = world.player.ship;
  const dx = x - me.com.x, dz = z - me.com.z;
  const h = me.heading, fx = Math.sin(h), fz = -Math.cos(h);
  Object.assign(aim, { x, z, world: view.rig.mode === 'free', ahead: dx * fx + dz * fz, right: -dx * fz + dz * fx, picked: true });
}

/** Lay the guns where the player is aiming, and fire when told. */
function updateAim(): void {
  const me = world.player;
  if (aim.side !== 0 && (!me.fighting || hud.spectating)) takeAim(0);
  if (aim.side === 0) {
    cmd.aim = null;
    return;
  }
  const moved = input.pointerLocked ? input.mouseDX !== 0 || input.mouseDY !== 0 : input.hovered;
  // Laid on a ship that's gone (sunk, lost to sight, out of reach): the mark stays where the guns last led her.
  const lock = locked;
  const target = lock ? world.vessels.find((v) => v.id === lock.id) : undefined;
  if (lock && (!target || !layable(target))) {
    const t = world.aimTarget;
    if (t) holdMark(t.leadX, t.leadZ);
    locked = null;
  }
  if (locked && target) {
    if (moved) pickOn(target);
    const t = world.aimTarget;
    if (t) holdMark(t.leadX, t.leadZ);
  } else if (!aim.picked || moved) {
    pickMark();
  }
  if (!aim.world) {
    const h = me.ship.heading, fx = Math.sin(h), fz = -Math.cos(h);
    aim.x = me.ship.com.x + fx * aim.ahead - fz * aim.right;
    aim.z = me.ship.com.z + fz * aim.ahead + fx * aim.right;
  }
  cmd.aim = { side: aim.side, x: aim.x, z: aim.z, target: locked && { ...locked }, part: aimPart };
  // A click on a ship, the guns not yet laid on one: lay them on her.
  const click = input.pressed('MouseLeft');
  const onto = click && !locked ? shipAt(4) : null;
  if (onto) {
    lockOnto(onto);
    return;
  }
  // Fire: a click (not a drag), or Space (which flies the free camera up).
  if (click || (input.pressed('Space') && view.rig.mode !== 'free')) {
    if (aim.side < 0) cmd.firePort = true;
    else cmd.fireStarboard = true;
  }
}

/** Ships drawn this frame (the rest are out of frame), for the HUD. */
function drawnShips(): { drawn: number; of: number } {
  return { drawn: view.models.size - view.culledCount, of: view.models.size };
}

/** Watching on after our ship is lost: the camera on the next ship still afloat (Y). */
function watchNext(): void {
  const list = world.vessels.filter((v) => v !== world.player && v.alive && !v.ship.sunk);
  if (!list.length) return;
  const i = list.findIndex((v) => v.id === view.rig.watchId);
  view.rig.watchId = list[(i + 1) % list.length]!.id;
  if (view.rig.mode === 'watch') view.rig.cut();
  else view.rig.setMode('watch', input);
}

const tmpV = new THREE.Vector3();

/**
 * Dev: time spent in each part of the frame (ms, summed while `profile.on`),
 * with the GPU waited for after rendering when `profile.gpu`. See __sim.profile.
 */
const profile = { on: false, gpu: false, frames: 0, ms: {} as Record<string, number>, t: 0 };
function lap(name: string): void {
  if (!profile.on) return;
  const now = performance.now();
  profile.ms[name] = (profile.ms[name] ?? 0) + now - profile.t;
  profile.t = now;
}

function step(dt: number, warmup = false): void {
  if (profile.on) (profile.t = performance.now()), profile.frames++;
  if (!warmup && started) {
    handleKeys(dt);
    updateAim();
  }
  lap('input');
  const alpha = world.update(dt, cmd);
  lap('sim');
  // Visual effects keep pace with the sim; the camera, controls and sound stay on real time.
  const simDt = dt * world.speed;
  const events = world.events.drain();
  for (const e of events) {
    if (e.type !== 'teleport' || e.vessel !== world.player.id) continue;
    view.cameraCut();
    env.carry(e.dx, e.dy, e.dz);
  }
  view.syncShips(alpha);
  lap('sync');
  const cam = view.rig.camera;
  effects.handle(events, cam, view.drainModelEvents());
  hud.handle(events);
  lap('events');
  view.rig.update(dt, world, view.ship, input);
  if (effects.shake > 0) view.rig.shake(effects.shake);
  effects.shake = 0;
  lap('camera');
  view.update(simDt);
  lap('view');
  env.update(simDt, view.ship.position, (x, y, z, r) => view.sees(x, y, z, r));
  lap('environment');
  const onShip = view.rig.mode === 'deck' || view.rig.mode === 'helm';
  effects.update(simDt, cam, onShip);
  threatView.update(world, cam, simDt, view.night);
  aimView.update(world, cam, dt, view.night);
  lap('effects');
  view.render(dt);
  if (profile.on && profile.gpu) renderer.getContext().finish();
  lap('render');
  hud.tracker.update(dt, cam, (v) => view.modelOf(v)?.posed);
  hud.aim.update(world, cam, view.rig.mode === 'free', aimPart);
  hud.hits.update(world, cam, simDt);
  hud.intents.update(world, cam, threatView.intents);
  lap('tracker');
  if (audio.started) {
    const fwd = cam.getWorldDirection(tmpV).clone();
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);
    audio.update(dt, world, { position: cam.position, forward: fwd, up }, events,
      (x, y, z) => new THREE.Vector3(x, y, z).applyMatrix4(view.ship.matrixWorld), onShip,
      () => env.randomGull());
  }
  lap('audio');
}

let last = performance.now();
function frame(now: number): void {
  const dt = Math.min(0.1, Math.max(0.001, (now - last) / 1000));
  last = now;
  step(dt);
  hud.update(view.rig.mode, view.rig.shotName, fpsAvg, renderScale, view.rig.freeSpeed, drawnShips());
  input.endFrame();

  // Dynamic resolution: keep the frame rate up by trading pixels.
  frameAvg += (dt - frameAvg) * 0.05;
  fpsAvg = 1 / frameAvg;
  scaleTimer += dt;
  if (scaleTimer > 1.5) {
    scaleTimer = 0;
    let next = renderScale;
    if (frameAvg > 1 / 45) next = Math.max(0.55, renderScale - 0.08);
    else if (frameAvg < 1 / 58 && renderScale < 1) next = Math.min(1, renderScale + 0.05);
    if (next !== renderScale) {
      renderScale = next;
      view.setRenderScale(renderScale);
      afterResize();
    }
  }
  requestAnimationFrame(frame);
}

/** ?weather=storm&hour=18.5&cam=cinematic&start - handy for sharing a scene. */
function applyUrlOptions(): void {
  const q = new URLSearchParams(location.search);
  const wx = q.get('weather');
  if (wx && (WEATHER_KINDS as readonly string[]).includes(wx)) {
    world.weather.dynamic = false;
    world.weather.set(wx as (typeof WEATHER_KINDS)[number], 0);
    world.wind.meanSpeed = world.weather.params.windSpeed;
    world.waves.settle({
      ...world.sea,
      windSpeed: world.weather.params.windSpeed,
      waveScale: world.weather.params.waveScale * world.settings.waveIntensity,
      swellHeight: world.weather.params.swellHeight,
      chop: world.weather.params.chop,
    });
  }
  const hour = Number(q.get('hour'));
  if (q.has('hour') && Number.isFinite(hour)) world.day.setHours(hour);
  const cam = q.get('cam');
  if (cam && CAMERA_MODES.some((c) => c.id === cam)) view.rig.setMode(cam as CameraMode, input);
  if (q.has('start')) (document.querySelector('.title-go') as HTMLButtonElement | null)?.click();
}

// Let the title card paint before the heavy start-up work.
setTimeout(() => void init(), 60);
(window as unknown as { __sim: unknown }).__sim = {
  world,
  audio,
  get view() {
    return view;
  },
  /** Dev: run the game for a while without waiting on animation frames. */
  advance(seconds: number, dt = 1 / 30): void {
    for (let t = 0; t < seconds; t += dt) {
      step(dt);
      if (profile.on) profile.t = performance.now();
      hud.update(view.rig.mode, view.rig.shotName, 1 / dt, renderScale, view.rig.freeSpeed, drawnShips());
      lap('hud');
      input.endFrame();
    }
  },
  /** Dev: per-part frame timing. `profile.on = true`, run, then `report()` (ms per frame). */
  profile,
  report(): Record<string, number> {
    const out: Record<string, number> = {};
    let total = 0;
    for (const [k, v] of Object.entries(profile.ms)) {
      out[k] = +(v / Math.max(1, profile.frames)).toFixed(3);
      total += v;
    }
    out.total = +(total / Math.max(1, profile.frames)).toFixed(3);
    profile.ms = {};
    profile.frames = 0;
    return out;
  },
};
if (import.meta.env.DEV) (window as unknown as { __debug: unknown }).__debug = { THREE, FullscreenPass, NOISE3D_FRAG, weatherTuning };
